import Button from "atoms/buttons/button";
import HitRecord from "models/view-models/search/hit-record";
import Icon from "atoms/icons/icon";
import MenuButton from "molecules/menu-button/menu-button";
import React, { useState } from "react";
import RichTextArea from "molecules/rich-text-area/rich-text-area";
import StringUtils from "utilities/string-utils";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ChatContextProvider } from "./chat-context";
import { CollectionUtils } from "utilities/collection-utils";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { Link } from "react-router-dom";
import { PublicationEntityTypeConstants } from "constants/publication-entity-type-constants";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { useCitationClick } from "utilities/hooks/domain/chat/use-citation-click";
import { useLocalization } from "utilities/hooks/use-localization";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------
export type CitationInfo = {
    citation: HitRecord;
    displayNumber: number;
};

export interface ChatCitationProps {
    citations?: CitationInfo[];
    onCitationClick?: () => void;
    conversationId?: string;
    citationPreview?: CitationInfo;
    setCitationPreview: (citation: CitationInfo | undefined) => void;
    previewRef?: React.RefObject<HTMLDivElement>;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-chat-modal-message-citation";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatCitation: React.FC<ChatCitationProps> = ({
    citations,
    onCitationClick,
    conversationId,
    citationPreview,
    setCitationPreview,
    previewRef,
}) => {
    const { t } = useLocalization();
    const [
        showAdditionalPublicationCitations,
        setShowAdditionalPublicationCitations,
    ] = useState(false);
    const [
        showAdditionalEnhancedCitations,
        setShowAdditionalEnhancedCitations,
    ] = useState(false);
    const [showAdditionalBlogCitations, setShowAdditionalBlogCitations] =
        useState(false);
    const isEnhancedContent = citationPreview?.citation.isEnhancedContent();

    const { handleOpenCitation } = useCitationClick();

    const handleCitationClick = (citation: HitRecord, index: number) => {
        setCitationPreview({ citation, displayNumber: index });
    };

    const handleBookViewClick = () => {
        handleOpenCitation(conversationId !== undefined);
        onCitationClick?.();
    };

    const getRoute = () => {
        // If we don't have a valid source, return an empty string
        if (!StringUtils.hasValue(citationPreview?.citation.source)) return "";

        // If this is a legacy publication section, generate the route with the region query parameters
        if (
            citationPreview.citation.entityType ===
            PublicationEntityTypeConstants.LegacyPublicationSection
        ) {
            return getLegacyPublicationSectionRoute(citationPreview.citation);
        }

        // Construct the query parameters for the route
        const queryParams = StringUtils.hasValue(conversationId)
            ? {
                  conversationId: conversationId,
              }
            : undefined;

        // Parse the 'source' property
        const parsed = new URL(
            citationPreview.citation.source,
            window.location.origin
        );

        // Construct the url
        return RouteUtils.getUrl(
            parsed.pathname, // path
            undefined, // path params
            queryParams, // query params
            parsed.hash // hash
        );
    };

    const getLegacyPublicationSectionRoute = (citation: HitRecord) => {
        if (
            !StringUtils.hasValue(citation.code) ||
            !StringUtils.hasValue(citation.edition)
        ) {
            return "";
        }

        const route = citation.entityId
            ? siteMap.publications.permalinks.legacyPublicationSectionPage
            : siteMap.publications.permalinks.legacyPublicationPage;

        const pageNumber = citation.pageNumber ?? 1;

        return RouteUtils.getUrl(route, {
            code: citation.code,
            edition: citation.edition,
            pageNumber: pageNumber,
            sectionId: citation.entityId,
        });
    };

    const publicationCitations = citations
        ?.filter(
            (citationInfo) =>
                citationInfo.citation.isPartOfPublication() ||
                citationInfo.citation.isTableEntity()
        )
        .sort((a, b) => a.displayNumber - b.displayNumber);

    const { inlinePublicationCitations, nonInlinePublicationCitations } =
        publicationCitations?.reduce(
            (acc, citationInfo) => {
                if (citationInfo.displayNumber > 0) {
                    acc.inlinePublicationCitations.push(citationInfo);
                } else {
                    acc.nonInlinePublicationCitations.push(citationInfo);
                }
                return acc;
            },
            {
                inlinePublicationCitations: [] as CitationInfo[],
                nonInlinePublicationCitations: [] as CitationInfo[],
            }
        ) ?? {
            inlinePublicationCitations: [],
            nonInlinePublicationCitations: [],
        };

    const enhancedContentCitations = citations
        ?.filter((citationInfo) => citationInfo.citation.isEnhancedContent())
        .sort((a, b) => a.displayNumber - b.displayNumber);

    const {
        inlineEnhancedContentCitations,
        nonInlineEnhancedContentCitations,
    } = enhancedContentCitations?.reduce(
        (acc, citationInfo) => {
            if (citationInfo.displayNumber > 0) {
                acc.inlineEnhancedContentCitations.push(citationInfo);
            } else {
                acc.nonInlineEnhancedContentCitations.push(citationInfo);
            }
            return acc;
        },
        {
            inlineEnhancedContentCitations: [] as CitationInfo[],
            nonInlineEnhancedContentCitations: [] as CitationInfo[],
        }
    ) ?? {
        inlineEnhancedContentCitations: [],
        nonInlineEnhancedContentCitations: [],
    };

    const isBlogCitation = (citationInfo: CitationInfo) =>
        citationInfo.citation.entityType?.toLowerCase() === "blog";

    const blogCitations = citations?.filter(isBlogCitation);

    const { inlineBlogCitations, nonInlineBlogCitations } =
        blogCitations?.reduce(
            (acc, citationInfo) => {
                if (citationInfo.displayNumber > 0) {
                    acc.inlineBlogCitations.push(citationInfo);
                } else {
                    acc.nonInlineBlogCitations.push(citationInfo);
                }
                return acc;
            },
            {
                inlineBlogCitations: [] as CitationInfo[],
                nonInlineBlogCitations: [] as CitationInfo[],
            }
        ) ?? {
            inlineBlogCitations: [],
            nonInlineBlogCitations: [],
        };

    return (
        <div className={BASE_CLASS} ref={previewRef}>
            {CollectionUtils.isNotEmpty(publicationCitations) && (
                <>
                    <div className={`${BASE_CLASS}__header`}>
                        <div>
                            {t("publication-resources")} (
                            {publicationCitations.length})
                        </div>
                        {publicationCitations.length >
                            inlinePublicationCitations.length && (
                            <Button
                                style={ButtonStyles.Text}
                                onClick={() =>
                                    setShowAdditionalPublicationCitations(
                                        !showAdditionalPublicationCitations
                                    )
                                }>
                                {showAdditionalPublicationCitations
                                    ? t("showLess")
                                    : t("showMore")}
                            </Button>
                        )}
                    </div>
                    <div className={`${BASE_CLASS}__grouping`}>
                        {inlinePublicationCitations.map((citationInfo) => (
                            <CitationButton
                                key={citationInfo.displayNumber}
                                citation={citationInfo.citation}
                                referenceNumber={citationInfo.displayNumber}
                                handleCitationClick={() =>
                                    handleCitationClick(
                                        citationInfo.citation,
                                        citationInfo.displayNumber
                                    )
                                }
                                focused={
                                    citationInfo.citation.id ===
                                    citationPreview?.citation.id
                                }
                            />
                        ))}
                        {showAdditionalPublicationCitations &&
                            nonInlinePublicationCitations.map(
                                (citationInfo) => (
                                    <CitationButton
                                        key={citationInfo.displayNumber}
                                        citation={citationInfo.citation}
                                        handleCitationClick={() =>
                                            handleCitationClick(
                                                citationInfo.citation,
                                                citationInfo.displayNumber
                                            )
                                        }
                                        focused={
                                            citationInfo.citation.id ===
                                            citationPreview?.citation.id
                                        }
                                    />
                                )
                            )}
                    </div>
                </>
            )}

            {CollectionUtils.isNotEmpty(enhancedContentCitations) && (
                <>
                    <div className={`${BASE_CLASS}__header`}>
                        <div>
                            {t("direct-enhanced-citation")} (
                            {enhancedContentCitations.length})
                        </div>
                        {enhancedContentCitations.length >
                            inlineEnhancedContentCitations.length && (
                            <Button
                                style={ButtonStyles.Text}
                                onClick={() =>
                                    setShowAdditionalEnhancedCitations(
                                        !showAdditionalEnhancedCitations
                                    )
                                }>
                                {showAdditionalEnhancedCitations
                                    ? t("showLess")
                                    : t("showMore")}
                            </Button>
                        )}
                    </div>
                    <div className={`${BASE_CLASS}__grouping`}>
                        {inlineEnhancedContentCitations.map((citationInfo) => (
                            <CitationButton
                                key={citationInfo.displayNumber}
                                citation={citationInfo.citation}
                                referenceNumber={citationInfo.displayNumber}
                                handleCitationClick={() =>
                                    handleCitationClick(
                                        citationInfo.citation,
                                        citationInfo.displayNumber
                                    )
                                }
                                focused={
                                    citationInfo.citation.id ===
                                    citationPreview?.citation.id
                                }></CitationButton>
                        ))}
                        {showAdditionalEnhancedCitations &&
                            nonInlineEnhancedContentCitations.map(
                                (citationInfo) => (
                                    <CitationButton
                                        key={citationInfo.displayNumber}
                                        citation={citationInfo.citation}
                                        handleCitationClick={() =>
                                            handleCitationClick(
                                                citationInfo.citation,
                                                citationInfo.displayNumber
                                            )
                                        }
                                        focused={
                                            citationInfo.citation.id ===
                                            citationPreview?.citation.id
                                        }
                                    />
                                )
                            )}
                    </div>
                </>
            )}

            {CollectionUtils.isNotEmpty(blogCitations) && (
                <>
                    <div className={`${BASE_CLASS}__header`}>
                        <div>
                            {t("blog-citations")} ({blogCitations.length})
                        </div>
                        {blogCitations.length > inlineBlogCitations.length && (
                            <Button
                                style={ButtonStyles.Text}
                                onClick={() =>
                                    setShowAdditionalBlogCitations(
                                        !showAdditionalBlogCitations
                                    )
                                }>
                                {showAdditionalBlogCitations
                                    ? t("showLess")
                                    : t("showMore")}
                            </Button>
                        )}
                    </div>
                    <div className={`${BASE_CLASS}__grouping`}>
                        {inlineBlogCitations.map((citationInfo) => (
                            <CitationAnchor
                                key={citationInfo.displayNumber}
                                citation={citationInfo.citation}
                                referenceNumber={citationInfo.displayNumber}
                                handleCitationClick={() =>
                                    handleCitationClick(
                                        citationInfo.citation,
                                        citationInfo.displayNumber
                                    )
                                }
                                focused={
                                    citationInfo.citation.id ===
                                    citationPreview?.citation.id
                                }
                            />
                        ))}
                        {showAdditionalBlogCitations &&
                            nonInlineBlogCitations.map((citationInfo) => (
                                <CitationAnchor
                                    key={citationInfo.displayNumber}
                                    citation={citationInfo.citation}
                                    handleCitationClick={() =>
                                        handleCitationClick(
                                            citationInfo.citation,
                                            citationInfo.displayNumber
                                        )
                                    }
                                    focused={
                                        citationInfo.citation.id ===
                                        citationPreview?.citation.id
                                    }
                                />
                            ))}
                    </div>
                </>
            )}

            {citationPreview && !isBlogCitation(citationPreview) && (
                <div
                    className={`${BASE_CLASS}__preview ${isEnhancedContent ? "-enhanced" : ""}`}>
                    <div className={`${BASE_CLASS}__preview-header`}>
                        <div
                            className={`${BASE_CLASS}__preview-header-publication`}>
                            {citationPreview.displayNumber > 0 && (
                                <span
                                    className={`${BASE_CLASS}__preview-header-publication__reference`}>
                                    {citationPreview.displayNumber}
                                </span>
                            )}
                            <span>
                                {`NFPA ${citationPreview?.citation.code} (${citationPreview?.citation.edition})`}
                            </span>
                        </div>
                        <div
                            className={`${BASE_CLASS}__preview-header-buttons`}>
                            <MenuButton
                                icon={Icons.MoreHorizontal}
                                cssClassName={`${BASE_CLASS}__preview-menu`}
                                iconSize={IconSizes.Medium}>
                                <Link
                                    to={getRoute()}
                                    className={`${BASE_CLASS}__preview-header-buttons-link`}>
                                    <Button
                                        style={ButtonStyles.None}
                                        onClick={() => handleBookViewClick()}
                                        cssClassName={`${BASE_CLASS}__preview-header-buttons-book`}>
                                        {t("book-view")}
                                    </Button>
                                </Link>
                            </MenuButton>
                            <Button
                                style={ButtonStyles.TertiaryAlt}
                                size={ButtonSizes.Small}
                                cssClassName={`${BASE_CLASS}__preview-close${isEnhancedContent ? "-enhanced" : ""}`}
                                onClick={() => setCitationPreview(undefined)}>
                                <Icon
                                    type={Icons.Close}
                                    size={IconSizes.Medium}
                                />
                            </Button>
                        </div>
                    </div>
                    <div className={`${BASE_CLASS}__preview-section-title`}>
                        {getTitle(citationPreview.citation)}
                    </div>
                    <div className={`${BASE_CLASS}__preview-content`}>
                        {citationPreview.citation.entityType ===
                        "EnhancedContent" ? (
                            <RichTextArea
                                content={citationPreview.citation.bodyXml ?? ""}
                            />
                        ) : (
                            <ChatContextProvider>
                                <div>
                                    {citationPreview.citation.getBodyXML()}
                                </div>
                            </ChatContextProvider>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatCitation;

// #endregion Exports

const CitationButton: React.FC<{
    citation: HitRecord;
    referenceNumber?: number;
    handleCitationClick: (citation: HitRecord) => void;
    focused: boolean;
}> = ({ citation, referenceNumber, handleCitationClick, focused }) => (
    <Button
        style={ButtonStyles.RoundedSecondary}
        cssClassName={`${BASE_CLASS}__button ${focused ? "-focus" : ""} ${referenceNumber === undefined ? "-no-reference" : ""}`}
        onClick={() => handleCitationClick(citation)}>
        {referenceNumber !== undefined && (
            <span className={`${BASE_CLASS}__button__reference`}>
                {referenceNumber}
            </span>
        )}
        <span className={`${BASE_CLASS}__button__title`}>
            {citation &&
                (citation.code && citation.edition
                    ? `NFPA ${citation.code} (${citation.edition}) `
                    : "")}
            {getTitle(citation)}
        </span>
    </Button>
);

const CitationAnchor: React.FC<{
    citation: HitRecord;
    referenceNumber?: number;
    handleCitationClick: (citation: HitRecord) => void;
    focused: boolean;
}> = ({ citation, referenceNumber, focused }) => {
    const getUrl = () => {
        if (!StringUtils.isValidUrl(citation.source)) return "";

        return citation.source;
    };

    const getBlogDomain = (url: string) => {
        if (!StringUtils.hasValue(url)) {
            return "";
        }
        try {
            const urlWithProtocol = url.startsWith("http")
                ? url
                : `https://${url}`;
            const urlObject = new URL(urlWithProtocol);
            return urlObject.hostname.replace(/^www\./, "");
        } catch (error) {
            return "";
        }
    };

    return (
        <>
            {StringUtils.isValidUrl(citation.source) ? (
                <a
                    href={getUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${BASE_CLASS}__anchor ${focused ? "-focus" : ""}`}>
                    {referenceNumber !== undefined && (
                        <span className={`${BASE_CLASS}__anchor__reference`}>
                            {referenceNumber}
                        </span>
                    )}
                    <span className={`${BASE_CLASS}__anchor__text`}>
                        <span className={`${BASE_CLASS}__anchor__title`}>
                            {citation?.title}
                        </span>
                        {StringUtils.hasValue(citation.source) && (
                            <span className={`${BASE_CLASS}__anchor__domain`}>
                                ({getBlogDomain(citation.source || "")})
                            </span>
                        )}
                    </span>
                </a>
            ) : (
                <span className={`${BASE_CLASS}__blog-anchor-no-link`}>
                    <span className={`${BASE_CLASS}__anchor__title`}>
                        {citation?.title}
                    </span>
                </span>
            )}
        </>
    );
};

/** Use the subtitle for sections to get the equivalent of SectionRecord.getFullyQualifiedTitle */
const getTitle = (citation: HitRecord) =>
    citation.entityType === "Section" ? citation.subtitle : citation.title;
